// =============================================================================
// Combobox de cliente del POS — diseñado para 1000s de clientes.
//
//  · Una sola caja siempre visible: 🔎 [input/chip] ×limpiar  [+ Nuevo]
//  · Search server-side (PostgREST `or()` sobre name/ruc/cedula/phone),
//    debounced 250 ms, paginado (PAGE=25) con "Cargar más" al pie.
//  · Cliente activo se muestra como chip naranja con × para quitarlo;
//    tap al chip o focus al input activa el modo búsqueda.
//  · × dentro del input limpia el texto sin cerrar el dropdown.
//  · Teclado: ↑↓ navega, Enter elige, Esc limpia y luego cierra.
//  · Estados claros: ocioso · "Buscando…" · "Sin resultados" · contador.
//  · Botón "+ Nuevo" prominente arriba (siempre) y al pie del dropdown
//    (pre-rellena el nombre con la query actual).
// =============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { crm, type Customer } from "../lib/api";

const PAGE = 25;

export function CustomerCombobox({
  value, onPick, onNew,
}: {
  value: Customer | null;
  onPick: (c: Customer | null) => void;
  onNew: (initialName?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cierre por click fuera
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Búsqueda debounced cuando el dropdown está abierto
  useEffect(() => {
    if (!open) return;
    setLoading(true); setReachedEnd(false); setHighlight(0);
    const id = window.setTimeout(async () => {
      try {
        const rs = await crm.searchCustomers({ search: q, limit: PAGE, offset: 0 });
        setItems(rs);
        if (rs.length < PAGE) setReachedEnd(true);
      } finally { setLoading(false); }
    }, 250);
    return () => window.clearTimeout(id);
  }, [q, open]);

  async function loadMore() {
    if (loading || reachedEnd) return;
    setLoading(true);
    try {
      const rs = await crm.searchCustomers({ search: q, limit: PAGE, offset: items.length });
      setItems((c) => [...c, ...rs]);
      if (rs.length < PAGE) setReachedEnd(true);
    } finally { setLoading(false); }
  }

  function openAndFocus() {
    setOpen(true); setQ("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function pick(c: Customer) {
    onPick(c); setOpen(false); setQ("");
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); const c = items[highlight]; if (c) pick(c); }
    else if (e.key === "Escape") {
      // 1° Esc limpia el texto; 2° Esc cierra.
      if (q) { setQ(""); inputRef.current?.focus(); } else { setOpen(false); }
    }
  }

  const activeChip = useMemo(() => {
    if (!value) return null;
    const idText = value.ruc ? `RUC ${value.ruc}-${value.dv ?? ""}`
                  : value.cedula ? `CIP ${value.cedula}` : "";
    return { name: value.name, id: idText };
  }, [value]);

  // Modo de visualización del input principal:
  //   · "chip"    → hay cliente y el dropdown está cerrado
  //   · "search"  → cualquier otra cosa (focus en el input)
  const mode: "chip" | "search" = activeChip && !open ? "chip" : "search";

  return (
    <div ref={wrapRef} className="relative">
      {/* CAJA PRINCIPAL */}
      <div className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2 shadow-sm focus-within:border-brand focus-within:ring-1 focus-within:ring-brand">
        <span className="text-grafito-400" aria-hidden>🔎</span>

        {mode === "chip" ? (
          // Chip naranja con el cliente activo; tap abre la búsqueda.
          <button onClick={openAndFocus}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md bg-brand/10 px-2 py-1 text-left text-sm hover:bg-brand/20">
            <span className="truncate font-semibold text-brand">{activeChip!.name}</span>
            {activeChip!.id && <span className="hidden truncate text-xs text-brand/70 sm:inline">{activeChip!.id}</span>}
          </button>
        ) : (
          // Input de búsqueda (con × para limpiar texto)
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <input ref={inputRef} value={q}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onKeyDown={onKeyDown}
              placeholder={value
                ? "Cambiar cliente — busca por nombre, RUC, cédula o teléfono…"
                : "Buscar cliente por nombre, RUC, cédula o teléfono…"}
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-grafito-400" />
            {q && (
              <button type="button" onClick={() => { setQ(""); inputRef.current?.focus(); }}
                className="rounded-full p-1 text-grafito-400 hover:bg-grafito-100 hover:text-grafito-700"
                title="Limpiar búsqueda" aria-label="Limpiar búsqueda">×</button>
            )}
          </div>
        )}

        {/* QUITAR cliente seleccionado (solo si hay y no estamos tipeando) */}
        {value && mode === "chip" && (
          <button type="button" onClick={() => { onPick(null); setOpen(false); setQ(""); }}
            className="rounded-full p-1 text-grafito-400 hover:bg-grafito-100 hover:text-red-600"
            title="Quitar cliente (consumidor final)" aria-label="Quitar cliente">×</button>
        )}

        {/* + NUEVO siempre visible */}
        <button type="button" onClick={() => onNew(q || undefined)}
          className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand/90"
          title="Crear cliente nuevo">
          <span aria-hidden>＋</span>
          <span className="hidden sm:inline">Nuevo</span>
        </button>
      </div>

      {/* DROPDOWN */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-xl border bg-white shadow-xl">
          <div className="flex items-center justify-between border-b px-3 py-2 text-[11px] text-grafito-500">
            <span>
              {loading ? "Buscando…" :
                q
                  ? `${items.length} resultado${items.length === 1 ? "" : "s"}${reachedEnd ? "" : "+"} para "${q}"`
                  : `Mostrando ${items.length}${reachedEnd ? "" : "+"} cliente${items.length === 1 ? "" : "s"}`
              }
            </span>
            <span className="hidden text-grafito-400 sm:inline">↑↓ ↵ esc</span>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {!loading && items.length === 0 && (
              <div className="p-4 text-center text-sm text-grafito-400">
                {q
                  ? <>Sin clientes que coincidan con <strong>"{q}"</strong>.</>
                  : "No hay clientes registrados todavía."}
              </div>
            )}
            {items.map((c, i) => (
              <button key={c.id} type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(c)}
                className={`flex w-full items-start gap-3 border-b px-3 py-2 text-left last:border-0 ${
                  i === highlight ? "bg-brand/10" : "hover:bg-grafito-50"
                }`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-grafito-900">{c.name}</div>
                  <div className="truncate text-[11px] text-grafito-400">
                    {c.ruc ? `RUC ${c.ruc}-${c.dv ?? ""}` : (c.cedula ? `CIP ${c.cedula}` : "Sin ID fiscal")}
                    {c.phone && ` · ${c.phone}`}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {c.commercial_segment && c.commercial_segment !== "minorista" && (
                    <span className="rounded bg-grafito-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-grafito-700">
                      {c.commercial_segment}
                    </span>
                  )}
                  {c.price_list_id && (
                    <span className="rounded bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                      lista
                    </span>
                  )}
                  {value?.id === c.id && <span className="text-brand">✓</span>}
                </div>
              </button>
            ))}
            {!reachedEnd && items.length > 0 && (
              <button type="button" onClick={loadMore} disabled={loading}
                className="w-full bg-grafito-50 px-3 py-2 text-xs font-semibold text-grafito-700 hover:bg-grafito-100 disabled:opacity-50">
                {loading ? "Cargando…" : "Cargar 25 más ↓"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
